import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Footer from "@/components/Footer";
import { setCanonical } from "@/lib/seo";

const Section = ({ n, title, children }) => (
  <section className="border-t border-navy/10 py-8" data-testid={`privacy-section-${n}`}>
    <div className="grid md:grid-cols-12 gap-6">
      <div className="md:col-span-1">
        <span className="font-display text-2xl text-orange">{n}</span>
      </div>
      <div className="md:col-span-11">
        <h2 className="font-display text-2xl text-navy mb-3">{title}</h2>
        <div className="text-ink-soft leading-relaxed space-y-3 text-[15px]">{children}</div>
      </div>
    </div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => { setCanonical("/privacy-policy"); }, []);
  return (
    <main className="bg-[var(--bg)] min-h-screen">
      <header className="bg-white/90 backdrop-blur-xl border-b border-navy/10 sticky top-0 z-40">
        <div className="max-w-8xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo" className="flex items-center">
            <img src="/logo-mp.png" alt="Martegiani Petroli Srl" className="h-10 md:h-11 w-auto" />
          </Link>
          <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-navy hover:text-orange transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="w-6 h-6 text-orange" strokeWidth={1.5} />
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange">Privacy Policy</p>
        </div>
        <h1 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
          Informativa sulla privacy
        </h1>
        <p className="text-ink-soft text-lg mt-6 leading-relaxed max-w-2xl">
          La presente informativa descrive le modalità di trattamento dei dati personali degli utenti
          che consultano questo sito e inviano richieste di preventivo, ai sensi del Regolamento (UE)
          2016/679 (GDPR).
        </p>

        <div className="mt-12 bg-white border border-navy/10 p-6 md:p-10">
          <Section n="01" title="Titolare del Trattamento">
            <p>
              Il Titolare del Trattamento dei dati è <strong>Martegiani Petroli Srl</strong>, con sede
              in Viale Luigi Settembrini 63, 64046 Montorio al Vomano (TE) — P.IVA 00587270679.
            </p>
            <p>
              Per qualsiasi richiesta relativa al trattamento dei dati è possibile contattare il
              Titolare ai recapiti: Tel. 0861-598527 · Email{" "}
              <a href="mailto:info@martegianipetroli.it" className="text-orange hover:underline">info@martegianipetroli.it</a>.
            </p>
          </Section>

          <Section n="02" title="Dati raccolti e finalità">
            <p>
              Attraverso i moduli di richiesta preventivo (gasolio, lubrificanti e AdBlue) raccogliamo
              esclusivamente i dati necessari a gestire la tua richiesta: <strong>Ragione Sociale,
              Partita IVA, Codice Fiscale, nome del richiedente, numero di telefono, indirizzo email</strong>{" "}
              ed eventualmente la sede/località del serbatoio da rifornire e il messaggio inserito.
            </p>
            <p>
              Tali dati vengono raccolti <strong>al solo scopo di rispondere alle richieste di
              preventivo</strong> e di fornire il relativo servizio commerciale. I dati{" "}
              <strong>non vengono ceduti, venduti o comunicati a terzi</strong> per finalità di
              marketing o di altra natura.
            </p>
          </Section>

          <Section n="03" title="Base giuridica del trattamento">
            <p>
              Il trattamento dei dati si fonda sul consenso espresso dall'utente al momento dell'invio
              del modulo (art. 6, par. 1, lett. a del GDPR) e sull'esecuzione di misure precontrattuali
              richieste dall'interessato (art. 6, par. 1, lett. b).
            </p>
          </Section>

          <Section n="04" title="Modalità e conservazione">
            <p>
              I dati sono trattati con strumenti informatici e trasmessi tramite connessione sicura
              (HTTPS). Sono conservati per il tempo strettamente necessario a gestire la richiesta e a
              soddisfare i relativi obblighi di legge, e successivamente cancellati o resi anonimi.
            </p>
          </Section>

          <Section n="05" title="Diritti dell'interessato">
            <p>
              In qualsiasi momento l'utente può esercitare i diritti previsti dagli artt. 15-22 del
              GDPR: accesso, rettifica, cancellazione, limitazione, opposizione e portabilità dei dati,
              nonché revoca del consenso. Le richieste vanno inoltrate a{" "}
              <a href="mailto:info@martegianipetroli.it" className="text-orange hover:underline">info@martegianipetroli.it</a>.
            </p>
          </Section>

          <Section n="06" title="Cookie">
            <p>
              Questo sito utilizza cookie tecnici necessari al corretto funzionamento. Al primo accesso
              viene mostrato un banner che consente di accettare o rifiutare i cookie non essenziali.
              La scelta viene memorizzata localmente sul dispositivo dell'utente e può essere modificata
              cancellando i dati del browser.
            </p>
          </Section>
        </div>

        <p className="text-ink-soft/70 text-sm mt-8">
          Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}.
        </p>
      </div>

      <Footer />
    </main>
  );
}
